import * as React from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowDropDown, Close } from "@mui/icons-material";
import { updateRfq } from "../../api/ProcurementClient";
import { getRFQStatusLabel, RFQStatus } from "../../models/RFQStatus";
import { useContext } from "react";
import { AppContext } from "../../AppContextProvider";
import ConfirmDialog from "../ConfirmDialog";
import { bumpRfqClientEpoch, mergeRfqIntoList } from "../../utils/mergeRfqIntoList";

interface CloseRfqSplitButtonProps {
  onClosed?: () => void;
  onCancelled?: () => void;
  rfqId: number;
  referenceNum: string;
  /** Used to revert optimistic updates on failure. */
  status: RFQStatus;
}

const confirmCopy: Record<
  RFQStatus.CLOSED | RFQStatus.CANCELLED,
  { title: string; description: string; okLabel: string }
> = {
  [RFQStatus.CLOSED]: {
    title: "Close RFQ",
    description: "Are you sure you want to close this RFQ?",
    okLabel: "Close RFQ",
  },
  [RFQStatus.CANCELLED]: {
    title: "Cancel RFQ",
    description: "Are you sure you want to cancel this RFQ?",
    okLabel: "Cancel RFQ",
  },
};

const CloseRfqSplitButton: React.FC<CloseRfqSplitButtonProps> = ({
  onClosed,
  onCancelled,
  rfqId,
  referenceNum,
  status,
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [statusPendingConfirm, setStatusPendingConfirm] = React.useState<
    RFQStatus.CLOSED | RFQStatus.CANCELLED | null
  >(null);

  const { setRfqs, setSnackbarMsg, setSnackbarErrorMsg } = useContext(AppContext);

  const requestStatusChange = (
    newStatus: RFQStatus.CLOSED | RFQStatus.CANCELLED
  ) => {
    setMenuAnchor(null);
    setStatusPendingConfirm(newStatus);
  };

  const handleConfirmDismiss = () => {
    setStatusPendingConfirm(null);
  };

  const handleStatusChange = async (
    newStatus: RFQStatus.CLOSED | RFQStatus.CANCELLED
  ) => {
    setStatusPendingConfirm(null);
    bumpRfqClientEpoch(rfqId, { statusEdit: true });
    setRfqs((prev) =>
      prev.map((q) => (q.id === rfqId ? { ...q, status: newStatus } : q))
    );
    try {
      const updated = await updateRfq(rfqId, { status: newStatus });
      bumpRfqClientEpoch(rfqId, { statusEdit: true });
      setRfqs((prev) => mergeRfqIntoList(prev, updated));
      setSnackbarMsg(
        `RFQ ${updated.referenceNum} status set to ${getRFQStatusLabel(newStatus)}.`,
      );
      if (newStatus === RFQStatus.CLOSED) {
        onClosed?.();
      } else {
        onCancelled?.();
      }
    } catch (e) {
      bumpRfqClientEpoch(rfqId, { statusEdit: true });
      setRfqs((prev) =>
        prev.map((q) => (q.id === rfqId ? { ...q, status } : q))
      );
      setSnackbarErrorMsg(
        e instanceof Error ? e.message : "Could not update RFQ status.",
      );
    }
  };

  return (
    <>
      <Box
        id="close-rfq-button-group"
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          alignSelf: "center",
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        <Tooltip title="Close RFQ" placement="bottom">
          <Box component="span" sx={{ display: "inline-flex" }}>
            <IconButton
              color="primary"
              aria-label="Close RFQ"
              onClick={() => requestStatusChange(RFQStatus.CLOSED)}
              size="small"
              sx={{
                borderRadius: 0,
                p: 0,
                pl: 0.75,
                minHeight: 0,
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
        <Tooltip title="More close options" placement="bottom">
          <Box component="span" sx={{ display: "inline-flex" }}>
            <IconButton
              color="primary"
              size="small"
              aria-label="More close options"
              aria-haspopup="true"
              aria-expanded={menuAnchor ? "true" : undefined}
              aria-controls={menuAnchor ? "close-rfq-menu" : undefined}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{
                borderRadius: 0,
                p: 0,
                px: 0,
                minHeight: 0,
              }}
            >
              <ArrowDropDown sx={{ fontSize: 22, mt: -0.25 }} />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>
      <Menu
        id="close-rfq-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem selected={true} onClick={() => requestStatusChange(RFQStatus.CLOSED)}>
          Close RFQ
        </MenuItem>
        <MenuItem onClick={() => requestStatusChange(RFQStatus.CANCELLED)}>
          Cancel RFQ
        </MenuItem>
      </Menu>
      {statusPendingConfirm != null ? (
        <ConfirmDialog
          title={confirmCopy[statusPendingConfirm].title}
          open
          okLabel={confirmCopy[statusPendingConfirm].okLabel}
          cancelLabel="Keep RFQ"
          onOk={() => {
            void handleStatusChange(statusPendingConfirm);
          }}
          onCancel={handleConfirmDismiss}
        >
          <Typography>
            {`${confirmCopy[statusPendingConfirm].description}${
              referenceNum ? ` (${referenceNum})` : ""
            }`}
          </Typography>
        </ConfirmDialog>
      ) : null}
    </>
  );
};

export default CloseRfqSplitButton;
